import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useUser } from "../context/UserContext";
import { useApi } from "../utils/Api";
import DeliveryForm from "../components/Checkout/DeliveryForm";
import ErrorModal from "../components/ErrorModal";
import GuestCheckoutModal from "../components/GuestCheckoutModal";
import SpinnerOverlay from "../components/SpinnerOverlay";

import CheckoutHeader from "../components/Checkout/CheckoutHeader";
import CheckoutTypeSelection from "../components/Checkout/CheckoutTypeSelection";
import EatInPresenceStep from "../components/Checkout/EatInPresenceStep";
import EatInForm from "../components/Checkout/EatInForm";
import PickupForm from "../components/Checkout/PickupForm";
import CheckoutSuccess from "../components/Checkout/CheckoutSuccess";
import CheckoutOrderSummary from "../components/Checkout/CheckoutOrderSummary";

const WHATSAPP_NUMBER = "447368325136";
const BLOCKED_DELIVERY_IDS = [9, 11, 12, 16];
const SERVICE_CHARGE_AMOUNT = 2.5;
const DELIVERY_SERVICE_CHARGE = 0;

const ALCOHOL_DELIVERY_MESSAGE =
  "Alcoholic drinks cannot be delivered. Please remove them to proceed with delivery.";
const DELIVERY_CLOSED_MESSAGE =
  "Delivery is only available from 1:00 PM to 9:30 PM UK time.";

export default function Checkout() {
  const { apiFetch } = useApi();
  const navigate = useNavigate();
  const location = useLocation();

  const { cartItems, clearCart, loading: cartLoading } = useCart();
  const { user, userLoading, openLoginModal } = useUser();

  const [stage, setStage] = useState(0);
  const [selectedCheckoutType, setSelectedCheckoutType] = useState(null);
  const [tableNumber, setTableNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [encryptedOrderId, setEncryptedOrderId] = useState(null);

  const [showGuestModal, setShowGuestModal] = useState(false);
  const [isGuest, setIsGuest] = useState(false);
  const [guestName, setGuestName] = useState("");
  const [guestChoiceMade, setGuestChoiceMade] = useState(false);

  const [addServiceCharge, setAddServiceCharge] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [postcode, setPostcode] = useState("");
  const [house, setHouse] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [deliveryFee, setDeliveryFee] = useState(null);
  const [lookingUp, setLookingUp] = useState(false);

  const guestCheckoutFromBasket = !!location.state?.guestCheckout;

  const [eatInOption, setEatInOption] = useState("dine-in");

  const hasInitializedRef = useRef(false);
  const responseSectionRef = useRef(null);
  const lastScrollKeyRef = useRef("");

  const allowedServiceChargePhones = ["+447495891188"];
  const canAddServiceCharge =
    !!user?.phone && allowedServiceChargePhones.includes(user.phone);

  const initialCheckoutType =
    location.state?.checkoutType ||
    new URLSearchParams(location.search).get("type") ||
    null;

  const getFriendlyCheckoutError = useCallback((message = "") => {
    const text = String(message).toLowerCase();

    if (text.includes("unavailable")) {
      return "Delivery service is unavailable right now. Please try again shortly.";
    }

    if (text.includes("timeout") || text.includes("timed out")) {
      return "We could not reach the delivery service right now. Please try again shortly.";
    }

    if (text.includes("payment link")) {
      return "Payment is unavailable right now. Please try again.";
    }

    if (text.includes("save order") || text.includes("failed")) {
      return "We could not process your delivery order right now. Please try again.";
    }

    return "Something went wrong. Please try again.";
  }, []);

  const getUkTime = useCallback(() => {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/London",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(new Date());

    const hour = Number(parts.find((p) => p.type === "hour")?.value ?? 0);
    const minute = Number(parts.find((p) => p.type === "minute")?.value ?? 0);

    return { hour, minute };
  }, []);

  const isDeliveryClosed = useCallback(() => {
    const { hour, minute } = getUkTime();
    return hour < 13 || hour > 21 || (hour === 21 && minute >= 30);
  }, [getUkTime]);

  const safeOpenLoginModal = useCallback(() => {
    if (userLoading) return;
    if (user) return;
    openLoginModal();
  }, [userLoading, user, openLoginModal]);

  const getItemTotals = useCallback((cartItem) => {
    const item = cartItem.item || cartItem;
    let total = Number(item.price || 0);
    const options = [];

    if (cartItem.selectedRequiredOption) {
      const requiredName =
        cartItem.selectedRequiredOption.option_name ||
        cartItem.selectedRequiredOption.name ||
        "";
      const requiredPrice = Number(
        cartItem.selectedRequiredOption.price ||
          cartItem.selectedRequiredOption.option_price ||
          0,
      );

      total += requiredPrice;

      options.push({
        name: requiredName,
        price: requiredPrice.toFixed(2),
      });
    }

    (item.swallowOptions || []).forEach((opt) => {
      const qty = Number(opt.quantity) || 1;
      const unitPrice = Number(opt.option_price || opt.price || 0);
      const optTotal = unitPrice * qty;

      total += optTotal;

      options.push({
        name: `${opt.option_name || opt.name || ""}${qty > 1 ? ` x${qty}` : ""}`,
        price: optTotal.toFixed(2),
      });
    });

    (item.proteins || []).forEach((opt) => {
      const qty = Number(opt.quantity) || 1;
      const unitPrice = Number(opt.option_price || opt.price || 0);
      const optTotal = unitPrice * qty;

      total += optTotal;

      options.push({
        name: `${opt.option_name || opt.name || ""}${qty > 1 ? ` x${qty}` : ""}`,
        price: optTotal.toFixed(2),
      });
    });

    (cartItem.selectedOptionalOptions || []).forEach((opt) => {
      const qty = Number(opt.quantity) || 1;
      const unitPrice = Number(opt.option_price || opt.price || 0);
      const optTotal = unitPrice * qty;

      total += optTotal;

      options.push({
        name: `${opt.option_name || opt.name || ""}${qty > 1 ? ` x${qty}` : ""}`,
        price: optTotal.toFixed(2),
      });
    });

    return { total, options };
  }, []);

  const cartTotal = useMemo(() => {
    return cartItems.reduce(
      (acc, c) => acc + getItemTotals(c).total * (c.quantity || 1),
      0,
    );
  }, [cartItems, getItemTotals]);

  const cartTotalWithService =
    cartTotal + (addServiceCharge ? SERVICE_CHARGE_AMOUNT : 0);

  const deliveryServiceCharge =
    selectedCheckoutType === "delivery" && deliveryFee !== null
      ? DELIVERY_SERVICE_CHARGE
      : 0;

  const deliveryGrandTotal =
    cartTotal + (deliveryFee || 0) + deliveryServiceCharge;

  const getUserFullName = () =>
    `${user?.first_name || ""} ${user?.last_name || ""}`.trim();

  const getDisplayName = () => {
    if (user) return getUserFullName();
    return guestName.trim();
  };

  const getDeliveryBlockReason = useCallback(() => {
    const blockedItem = cartItems.find((c) =>
      BLOCKED_DELIVERY_IDS.includes(Number(c.item?.category_id)),
    );

    if (blockedItem) return ALCOHOL_DELIVERY_MESSAGE;
    if (isDeliveryClosed()) return DELIVERY_CLOSED_MESSAGE;

    return "";
  }, [cartItems, isDeliveryClosed]);

  const handleOrderSuccess = useCallback(
    (newEncryptedOrderId) => {
      setEncryptedOrderId(newEncryptedOrderId || null);
      setSuccess(true);
      setTimeout(() => clearCart(), 1000);
    },
    [clearCart],
  );

  useEffect(() => {
    if (cartLoading) return;

    const timer = setTimeout(() => {
      if (!cartItems.length && !success) {
        navigate("/order-online", { replace: true });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [cartLoading, cartItems, success, navigate]);

  useEffect(() => {
    if (user?.id) {
      setShowGuestModal(false);
      setIsGuest(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (hasInitializedRef.current) return;
    if (userLoading) return;

    hasInitializedRef.current = true;

    if (!initialCheckoutType) {
      setSelectedCheckoutType(null);
      setStage(0);
      return;
    }

    if (initialCheckoutType === "delivery") {
      const blockReason = getDeliveryBlockReason();

      if (blockReason) {
        setSelectedCheckoutType(null);
        setStage(0);
        setCheckoutError(blockReason);
        return;
      }

      if (!user) {
        setSelectedCheckoutType(null);
        setStage(0);
        return;
      }

      setSelectedCheckoutType("delivery");
      setStage(1);
      return;
    }

    if (initialCheckoutType === "pickup") {
      if (!user) {
        setSelectedCheckoutType(null);
        setStage(0);
        return;
      }

      setSelectedCheckoutType("pickup");
      setStage(1);
      return;
    }

    if (initialCheckoutType === "eat-in") {
      setSelectedCheckoutType("eat-in");

      if (!user && guestCheckoutFromBasket) {
        setIsGuest(true);
        setGuestChoiceMade(true);
        setShowGuestModal(false);
        setStage(1);
        return;
      }

      setStage(1);

      if (!user && !guestChoiceMade) {
        setShowGuestModal(true);
      }
    }
  }, [
    initialCheckoutType,
    user,
    userLoading,
    guestChoiceMade,
    getDeliveryBlockReason,
    guestCheckoutFromBasket,
  ]);

  useEffect(() => {
    if (success || !selectedCheckoutType) return;

    const isPhone = window.matchMedia("(max-width: 767px)").matches;
    if (!isPhone) return;

    let shouldScroll = false;
    let scrollKey = "";

    if (selectedCheckoutType === "pickup") {
      shouldScroll = true;
      scrollKey = "pickup";
    } else if (selectedCheckoutType === "delivery") {
      shouldScroll = true;
      scrollKey = "delivery";
    } else if (selectedCheckoutType === "eat-in" && stage >= 1) {
      shouldScroll = true;
      scrollKey = `eat-in-${stage}`;
    }

    if (!shouldScroll) return;
    if (lastScrollKeyRef.current === scrollKey) return;

    const timer = setTimeout(() => {
      const el = responseSectionRef.current;
      if (!el) return;

      lastScrollKeyRef.current = scrollKey;

      const y = el.getBoundingClientRect().top + window.pageYOffset - 110;

      window.scrollTo({
        top: y,
        behavior: "smooth",
      });
    }, 150);

    return () => clearTimeout(timer);
  }, [selectedCheckoutType, stage, success]);

  const handleSelectOrderType = (type) => {
    if (userLoading) return;

    lastScrollKeyRef.current = "";
    setCheckoutError("");
    setSuccess(false);

    if (type === "delivery") {
      const blockReason = getDeliveryBlockReason();

      if (blockReason) {
        setSelectedCheckoutType(null);
        setStage(0);
        setCheckoutError(blockReason);
        return;
      }

      if (!user) {
        safeOpenLoginModal();
        return;
      }

      setSelectedCheckoutType("delivery");
      setStage(1);
      return;
    }

    if (type === "pickup") {
      if (!user) {
        safeOpenLoginModal();
        return;
      }

      setSelectedCheckoutType("pickup");
      setStage(1);
      return;
    }

    if (type === "eat-in") {
      setSelectedCheckoutType("eat-in");
      setStage(1);

      if (!user && !guestChoiceMade && !isGuest) {
        setShowGuestModal(true);
      }
    }
  };

  const handleEatInStage1Answer = (answer) => {
    setStage(answer ? 2 : 3);
  };

  const goBack = () => {
    lastScrollKeyRef.current = "";
    setCheckoutError("");

    if (success) {
      navigate("/order-online");
      return;
    }

    if (selectedCheckoutType === "eat-in" && stage > 1) {
      setStage(1);
      return;
    }

    if (selectedCheckoutType) {
      setSelectedCheckoutType(null);
      setStage(0);
      return;
    }

    navigate(-1);
  };

  const placeEatInOrPickupOrder = async () => {
    if (isLoading || userLoading) return;
    setIsLoading(true);

    if (!cartItems.length) {
      setCheckoutError("Your cart is empty.");
      setIsLoading(false);
      return;
    }

    if (selectedCheckoutType === "pickup" && !user) {
      safeOpenLoginModal();
      setIsLoading(false);
      return;
    }

    if (selectedCheckoutType === "eat-in" && !user && !isGuest) {
      setShowGuestModal(true);
      setIsLoading(false);
      return;
    }

    const payload = {
      customer_id: user?.id || null,
      guest_name: !user ? guestName.trim() : null,
      order_type:
        selectedCheckoutType === "eat-in"
          ? eatInOption === "take-away"
            ? "Take Away"
            : "Eat In"
          : "Pick Up",
      table_number:
        selectedCheckoutType === "eat-in" ? tableNumber.trim() : null,
      notes: notes.trim(),
      items: cartItems.map((c) => ({
        item_id: c.item?.id,
        base_price: c.item?.price || 0,
        required_option_id: c.selectedRequiredOption?.id || null,
        required_option_name:
          c.selectedRequiredOption?.option_name ||
          c.selectedRequiredOption?.name ||
          "",
        required_option_price:
          c.selectedRequiredOption?.price ||
          c.selectedRequiredOption?.option_price ||
          0,
        quantity: c.quantity || 1,
        selectedOptionalOptions: c.selectedOptionalOptions || [],
        swallowOptions: c.item?.swallowOptions || [],
        proteins: c.item?.proteins || [],
      })),
      total_price: cartTotalWithService,
      service_charge: addServiceCharge ? SERVICE_CHARGE_AMOUNT : 0,
      status: "to do",
      payment_method: "cash",
    };

    try {
      const result = await apiFetch("place-order", payload);

      if (result.success) {
        handleOrderSuccess(result.encrypted_order_id);
      } else {
        setCheckoutError("Failed to place order.");
      }
    } catch (err) {
      console.error("Error sending order:", err);
      setCheckoutError("Something went wrong while placing your order.");
    } finally {
      setIsLoading(false);
    }
  };

  // const placePickupOrder = async () => {
  //   if (isLoading || userLoading) return;

  //   if (!user) {
  //     safeOpenLoginModal();
  //     return;
  //   }

  //   if (!cartItems.length) {
  //     setCheckoutError("Your cart is empty.");
  //     return;
  //   }

  //   setIsLoading(true);
  //   setCheckoutError("");

  //   try {
  //     const orderItems = cartItems.map((c) => ({
  //       item_id: c.item?.id || c.item_id,
  //       base_price: c.item?.price || 0,
  //       required_option_id: c.selectedRequiredOption?.id || null,
  //       required_option_name:
  //         c.selectedRequiredOption?.option_name ||
  //         c.selectedRequiredOption?.name ||
  //         "",
  //       required_option_price:
  //         c.selectedRequiredOption?.price ||
  //         c.selectedRequiredOption?.option_price ||
  //         0,
  //       quantity: c.quantity || 1,
  //       selectedOptionalOptions: c.selectedOptionalOptions || [],
  //       swallowOptions: c.item?.swallowOptions || [],
  //       proteins: c.item?.proteins || [],
  //     }));

  //     const result = await apiFetch("place-pickup-order", {
  //       customer_id: user?.id || null,
  //       order_type: "Pick Up",
  //       items: orderItems,
  //       notes: notes.trim(),
  //       payment_method: "online",
  //     });

  //     if (!result?.success) {
  //       setCheckoutError(
  //         "We could not process your pickup order right now. Please try again.",
  //       );
  //       return;
  //     }

  //     if (result?.stripe_payment_link) {
  //       window.location.href = result.stripe_payment_link;
  //       return;
  //     }

  //     setCheckoutError("Payment is unavailable right now. Please try again.");
  //   } catch (err) {
  //     console.error(err);
  //     setCheckoutError(
  //       "We could not process your pickup order right now. Please try again.",
  //     );
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const placeDeliveryOrder = async () => {
    if (isLoading || userLoading) return;

    if (!user) {
      safeOpenLoginModal();
      return;
    }

    if (!postcode.trim()) {
      setCheckoutError("Please enter your postcode.");
      return;
    }

    if (!house.trim()) {
      setCheckoutError("Please enter your house/flat.");
      return;
    }

    if (!deliveryAddress.trim()) {
      setCheckoutError("Please find your address first.");
      return;
    }

    if (deliveryFee === null) {
      setCheckoutError("Delivery fee not available yet.");
      return;
    }

    if (!cartItems.length) {
      setCheckoutError("Your cart is empty.");
      return;
    }

    setIsLoading(true);
    setCheckoutError("");

    try {
      const orderItems = cartItems.map((c) => ({
        item_id: c.item?.id || c.item_id,
        base_price: c.item?.price || 0,
        required_option_id: c.selectedRequiredOption?.id || null,
        required_option_name:
          c.selectedRequiredOption?.option_name ||
          c.selectedRequiredOption?.name ||
          "",
        required_option_price:
          c.selectedRequiredOption?.price ||
          c.selectedRequiredOption?.option_price ||
          0,
        quantity: c.quantity || 1,
        selectedOptionalOptions: c.selectedOptionalOptions || [],
        swallowOptions: c.item?.swallowOptions || [],
        proteins: c.item?.proteins || [],
      }));

      const dbResult = await apiFetch("place-delivery-order", {
        customer_id: user?.id || null,
        customer_name: getUserFullName(),
        customer_phone: user?.phone || "",
        order_type: "Delivery",
        delivery_address: deliveryAddress,
        postcode: postcode.trim(),
        items: orderItems,
        total_price: cartTotal.toFixed(2),
        delivery_fee: (deliveryFee || 0).toFixed(2),
        service_charge: deliveryServiceCharge.toFixed(2),
        notes: notes.trim(),
        payment_method: "online",
      });

      if (!dbResult?.success) {
        const backendMessage = String(dbResult?.message || "").toLowerCase();

        if (
          backendMessage.includes("confirm delivery postcode") ||
          backendMessage.includes("delivery fee expired") ||
          backendMessage.includes("verified postcode missing")
        ) {
          setDeliveryFee(null);
          setDeliveryAddress("");
          setCheckoutError(
            "Session timed out. Please find your delivery address again.",
          );
          return;
        }

        setCheckoutError(getFriendlyCheckoutError(dbResult?.message));
        return;
      }

      if (dbResult?.stripe_payment_link) {
        sessionStorage.setItem("stripe_delivery_return_pending", "1");
        window.location.href = dbResult.stripe_payment_link;
        return;
      }

      setCheckoutError("Payment is unavailable right now. Please try again.");
    } catch (err) {
      console.error(err);
      setCheckoutError(
        "We could not process your delivery order right now. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const canSubmitEatIn =
    !isLoading &&
    (user ? true : guestName.trim() !== "") &&
    (eatInOption === "take-away" || tableNumber.trim() !== "");

  const canSubmitPickup = !isLoading && !!user;

  const pageTitle = "Checkout";

  if (cartLoading || userLoading) return <SpinnerOverlay />;

  return (
    <>
      {isLoading && <SpinnerOverlay />}

      {!!checkoutError && (
        <ErrorModal
          message={checkoutError}
          onClose={() => setCheckoutError("")}
        />
      )}

      <GuestCheckoutModal
        isOpen={showGuestModal}
        onClose={() => {
          setShowGuestModal(false);
          setSelectedCheckoutType(null);
          setStage(0);
          navigate("/order-online");
        }}
        onContinueGuest={() => {
          setIsGuest(true);
          setGuestChoiceMade(true);
          setShowGuestModal(false);
          setSelectedCheckoutType("eat-in");
          setStage(1);
        }}
        onLogin={() => {
          setGuestChoiceMade(true);
          setShowGuestModal(false);
          safeOpenLoginModal();
        }}
      />

      <main className="min-h-screen bg-black text-white">
        <div className="mx-auto max-w-[1400px] px-5 pb-12 pt-[120px] lg:px-10">
          <CheckoutHeader title={pageTitle} onBack={goBack} success={success} />

          {success ? (
            <div className="mt-8 flex justify-center">
              <div className="w-full max-w-3xl">
                <div className="rounded-3xl border border-white/10 bg-white p-4 text-black shadow-[0_20px_60px_rgba(0,0,0,0.35)] sm:p-6">
                  <CheckoutSuccess
                    getUkTime={getUkTime}
                    whatsappNumber={WHATSAPP_NUMBER}
                    encryptedOrderId={encryptedOrderId}
                    onTrack={() => {
                      if (encryptedOrderId) {
                        navigate(`/order-tracking?id=${encryptedOrderId}`);
                      }
                    }}
                    onBackToMenu={() => navigate("/order-online")}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-8 grid grid-cols-1 items-start gap-8 lg:grid-cols-12 lg:gap-10">
              <div className="space-y-6 lg:col-span-8">
                <div className="rounded-3xl border border-white/10 bg-white/5 p-4 backdrop-blur-sm sm:p-6">
                  <CheckoutTypeSelection
                    onSelect={handleSelectOrderType}
                    selectedType={selectedCheckoutType}
                  />
                </div>

                <div ref={responseSectionRef}>
                  {selectedCheckoutType === "eat-in" && stage === 1 && (
                    <div className="rounded-3xl border border-white/10 bg-white/5 p-4 backdrop-blur-sm sm:p-6">
                      <EatInPresenceStep
                        disabled={!user && !isGuest}
                        onAnswer={handleEatInStage1Answer}
                      />
                    </div>
                  )}

                  {selectedCheckoutType === "eat-in" && stage === 2 && (
                    <div className="rounded-3xl border border-white/10 bg-white p-4 text-black shadow-[0_20px_60px_rgba(0,0,0,0.35)] sm:p-6">
                      <EatInForm
                        user={user}
                        guestName={guestName}
                        setGuestName={setGuestName}
                        tableNumber={tableNumber}
                        setTableNumber={setTableNumber}
                        notes={notes}
                        setNotes={setNotes}
                        canAddServiceCharge={canAddServiceCharge}
                        addServiceCharge={addServiceCharge}
                        setAddServiceCharge={setAddServiceCharge}
                        cartTotal={cartTotal}
                        cartTotalWithService={cartTotalWithService}
                        serviceChargeAmount={SERVICE_CHARGE_AMOUNT}
                        getUserFullName={getUserFullName}
                        eatInOption={eatInOption}
                        setEatInOption={setEatInOption}
                      />
                    </div>
                  )}

                  {selectedCheckoutType === "eat-in" && stage === 3 && (
                    <section className="rounded-3xl border border-[#fa9324]/30 bg-white p-6 text-black shadow-[0_20px_60px_rgba(0,0,0,0.35)]">
                      <h2 className="mb-2 text-lg font-semibold text-black">
                        Cannot Order
                      </h2>
                      <p className="text-sm text-black/70 sm:text-base">
                        You must be in the restaurant to place an Eat-In order.
                      </p>
                    </section>
                  )}

                  {selectedCheckoutType === "pickup" && (
                    <div className="rounded-3xl border border-white/10 bg-white p-4 text-black shadow-[0_20px_60px_rgba(0,0,0,0.35)] sm:p-6">
                      <PickupForm
                        displayName={getDisplayName()}
                        notes={notes}
                        setNotes={setNotes}
                      />
                    </div>
                  )}

                  {selectedCheckoutType === "delivery" && (
                    <section className="rounded-3xl border border-white/10 bg-white p-4 text-black shadow-[0_20px_60px_rgba(0,0,0,0.35)] sm:p-6">
                      <DeliveryForm
                        postcode={postcode}
                        setPostcode={setPostcode}
                        house={house}
                        setHouse={setHouse}
                        deliveryAddress={deliveryAddress}
                        setDeliveryAddress={setDeliveryAddress}
                        deliveryFee={deliveryFee}
                        setDeliveryFee={setDeliveryFee}
                        notes={notes}
                        setNotes={setNotes}
                        setErrorMessage={setCheckoutError}
                        lookingUp={lookingUp}
                        setLookingUp={setLookingUp}
                        setIsLoading={setIsLoading}
                      />
                    </section>
                  )}
                </div>
              </div>

              {selectedCheckoutType && (
                <div className="lg:col-span-4">
                  <div className="lg:sticky lg:top-28">
                    <CheckoutOrderSummary
                      checkoutType={selectedCheckoutType}
                      cartItems={cartItems}
                      getItemTotals={getItemTotals}
                      addServiceCharge={
                        selectedCheckoutType === "delivery"
                          ? deliveryServiceCharge > 0
                          : addServiceCharge
                      }
                      serviceChargeAmount={
                        selectedCheckoutType === "delivery"
                          ? deliveryServiceCharge
                          : SERVICE_CHARGE_AMOUNT
                      }
                      total={
                        selectedCheckoutType === "delivery"
                          ? deliveryGrandTotal
                          : cartTotalWithService
                      }
                      isLoading={isLoading}
                      placingOrder={isLoading}
                      checkingStock={lookingUp}
                      onPlaceOrder={
                        selectedCheckoutType === "delivery"
                          ? placeDeliveryOrder
                          : placeEatInOrPickupOrder
                      }
                      buttonText={
                        selectedCheckoutType === "delivery"
                          ? "Pay Online"
                          : "Send Order To Kitchen"
                      }
                      disablePay={
                        selectedCheckoutType === "eat-in"
                          ? !canSubmitEatIn || stage !== 2
                          : selectedCheckoutType === "pickup"
                            ? !canSubmitPickup
                            : !user ||
                              !postcode.trim() ||
                              !house.trim() ||
                              !deliveryAddress.trim() ||
                              deliveryFee === null ||
                              lookingUp
                      }
                      deliveryFee={
                        selectedCheckoutType === "delivery" ? deliveryFee : null
                      }
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </>
  );
}
